/* eslint-disable @typescript-eslint/no-explicit-any */

import { inject } from '@angular/core';
import { CanActivateFn, Params, Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';

/**
 * Resolves the active portfolio for portfolio-scoped routes. Reads URL params
 * (?id / ?name / ?portfolioId), falls back to the alphabetically-first folio,
 * and writes the result via PortfolioApiService.setActivePortfolio.
 */
export const folioGuard: CanActivateFn = (_next, state) => {
  const featureFlagsService = inject(FeatureFlagsService);
  const portfolioApi = inject(PortfolioApiService);
  const router = inject(Router);

  const currentUrl = state.url.split('?')[0];

  const pickDefaultFolio = (folios: any[]): any => {
    return folios.slice().sort((a, b) => a.name.localeCompare(b.name))[0];
  };

  const buildPreservedQueryParams = (
    source: Params,
    overrides: Record<string, string | null | undefined>,
  ): Params => {
    const defaultSort =
      featureFlagsService.scorelabSettings.defaultScore === 'sentiment'
        ? '-senti'
        : 'score';
    return {
      news: source.news,
      highlight: source.highlight,
      time: source.time,
      filter: source.filter,
      lang: source.lang ? source.lang : 'en,de',
      feed: source.feed
        ? source.feed
        : 'non_professional,online,print,professional',
      ranks: source.ranks,
      panels: source.panels,
      categories: source.categories ? source.categories : '',
      continents: source.continents,
      countries: source.countries,
      factuality: source.factuality,
      temporality: source.temporality,
      event_ids: source.event_ids,
      sort: defaultSort,
      context: source.context,
      ...overrides,
    };
  };

  const resolveActiveFolio = (folios: any[], queryParams: Params): void => {
    if (!folios?.length) {
      // undefined = "resolved: user has none" (empty state); null = "loading"
      portfolioApi.setActivePortfolio(undefined);
      return;
    }

    const { id, name, portfolioId } = queryParams;

    if (!id && name) {
      portfolioApi.setActivePortfolio({
        email: null,
        uid: null,
        name,
        content: (queryParams.entities ?? '').split(','),
      });
      return;
    }

    const targetUid = portfolioId ?? id;
    const target = targetUid
      ? folios.find((f) => f.uid === targetUid)
      : undefined;

    if (target) {
      portfolioApi.setActivePortfolio(target);
      if (portfolioId && !id) {
        router.navigate([currentUrl], {
          queryParams: buildPreservedQueryParams(queryParams, {
            id: target.uid,
            portfolioId: null,
          }),
        });
      }
      return;
    }

    if (portfolioId) {
      alert(
        'The link includes a portfolio that is not accessible. You will be redirected to your cockpit.',
      );
    }
    const fallback = pickDefaultFolio(folios);
    portfolioApi.setActivePortfolio(fallback);
    const overrides = portfolioId
      ? { portfolioId: fallback.uid, id: null, type: queryParams.type }
      : { id: fallback.uid };
    router.navigate([currentUrl], {
      queryParams: buildPreservedQueryParams(queryParams, overrides),
    });
  };

  return portfolioApi.portFolios().pipe(
    map((folios) => {
      resolveActiveFolio(folios, state.root.queryParams);
      return true;
    }),
  );
};
